import React, { useMemo, useRef, useEffect, useState } from "react";
import styles from "./Analysis.module.css";
import { Button, DrawerBody, OverlayDrawer } from "@fluentui/react-components";
import {
    Home20Regular, Sparkle20Regular, Checkmark16Regular, Add16Regular, Send20Regular, Copy20Regular, Flash20Regular, Apps20Regular, Payment20Regular,
    } from "@fluentui/react-icons";

interface AppAnalysisProps {
    isOpen: boolean;
    onClose: () => void;
}

/* ------------------------------------------------------------------ *
 *  AI Sales Strategy – what comparable businesses do that you don't.
 *  Everything below is sample data. API wiring points are marked
 *  "API:" – replace GAPS / BENCH / SCORE / askStrategyAssistant with
 *  your own endpoints (e.g. GET /analytics/sales-gaps).
 * ------------------------------------------------------------------ */

type Effort = "Low" | "Medium" | "High";

interface Gap {
    id: string;
    title: string;
    peers: string;
    adoption: number;
    you: "Not doing" | "Partly";
    impact: number;
    effort: Effort;
    days: number;
    module: string;
    steps: string[];
}

/* API: GET /analytics/sales-gaps?peerSet=... */
const GAPS: Gap[] = [
    {
        id: "g1", title: "Direct-booking offer with a best-rate promise",
        peers: "Top performers show a \"book direct, get the best rate + free breakfast\" banner and cut OTA commission.",
        adoption: 68, you: "Not doing", impact: 1850000, effort: "Medium", days: 14, module: "Hospitality",
        steps: ["Publish a booking link on your site, Google profile and WhatsApp", "Add a member rate that is 5–8% below OTA prices", "Add a free perk (breakfast or late check-out) for direct guests"],
    },
    {
        id: "g2", title: "Reply to every lead within 10 minutes",
        peers: "Peers reply in about 9 minutes; your average first reply is about 3 hours, and most buyers choose whoever answers first.",
        adoption: 74, you: "Partly", impact: 1320000, effort: "Low", days: 7, module: "CRM",
        steps: ["Turn on instant auto-reply with a booking or quote link", "Assign an owner to every new lead", "Alert the manager when a lead waits more than 15 minutes"],
    },
    {
        id: "g3", title: "Upsell at check-in and before arrival",
        peers: "63% of peers sell late check-out, breakfast, airport pickup or room upgrades at the moment of booking.",
        adoption: 63, you: "Not doing", impact: 940000, effort: "Low", days: 7, module: "Hospitality",
        steps: ["Create 4 add-on packages with fixed prices", "Offer them in the confirmation message and at the front desk", "Track attach rate per receptionist weekly"],
    },
    {
        id: "g4", title: "Win-back campaign for past customers",
        peers: "Peers message guests and buyers who have not returned in 60–90 days with a personal offer.",
        adoption: 58, you: "Not doing", impact: 1100000, effort: "Low", days: 10, module: "CRM",
        steps: ["Segment customers with no activity for 60+ days", "Send a WhatsApp or email offer valid for 14 days", "Measure repeat bookings from the campaign"],
    },
    {
        id: "g5", title: "Corporate accounts with negotiated rates",
        peers: "47% of peers sign 5–10 local companies for monthly volume in exchange for a fixed rate.",
        adoption: 47, you: "Partly", impact: 2400000, effort: "High", days: 30, module: "CRM",
        steps: ["List 30 companies near you that travel or host visitors", "Prepare a one-page corporate rate offer", "Visit or call 5 per week and log outcomes in CRM"],
    },
    {
        id: "g6", title: "Dynamic pricing on busy and slow days",
        peers: "54% of peers raise prices when occupancy passes 70% and discount slow midweek nights.",
        adoption: 54, you: "Not doing", impact: 1600000, effort: "Medium", days: 14, module: "Hospitality",
        steps: ["Define 3 price tiers by occupancy level", "Apply the high tier on weekends and local events", "Review rates every Monday against last week's occupancy"],
    },
];

/* API: GET /analytics/benchmarks – you vs the median of comparable businesses */
interface Bench { id: string; label: string; unit: string; you: number; peers: number; lowerBetter?: boolean }
const BENCH: Bench[] = [
    { id: "b1", label: "Direct share", unit: "%", you: 22, peers: 41 },
    { id: "b2", label: "Conversion", unit: "%", you: 9, peers: 14 },
    { id: "b3", label: "Repeat rate", unit: "%", you: 18, peers: 31 },
    { id: "b4", label: "Upsell rate", unit: "%", you: 6, peers: 17 },
    { id: "b5", label: "First reply", unit: "m", you: 180, peers: 9, lowerBetter: true },
];

const SCORE = { you: 62, peers: 78 };
const PEER_SET = "Similar hospitality businesses in Nigeria";
const PEER_SET_LOWER = "similar hospitality businesses in Nigeria";

/* chart colours (from the reference design) */
const NAVY = "#2b3553";
const TEAL = "#14b8c6";
const BLUE = "#2f6fd6";
const SKY = "#9ec1ee";
const DONUT = ["#1e2a52", BLUE, TEAL, SKY, "#0e93b5", "#c9d4e8"];

const naira = (n: number) => "₦" + n.toLocaleString("en-NG");
const short = (n: number) => (n >= 1000000 ? `₦${(n / 1000000).toFixed(2).replace(/\.?0+$/, "")}M` : `₦${Math.round(n / 1000)}K`);
const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

type View = "insights" | "assistant";
type Tab = "all" | "plan" | "quick";
type Basis = "effort" | "module";
interface Msg { id: number; from: "ai" | "me"; text: string }

/* API: POST /analytics/assistant { question } -> { answer } */
const askStrategyAssistant = (q: string): string => {
    const t = q.toLowerCase();
    const sorted = [...GAPS].sort((a, b) => b.impact / b.days - a.impact / a.days);
    const find = (re: RegExp) => GAPS.find(g => re.test(`${g.title} ${g.peers}`.toLowerCase()));
    let g: Gap | undefined;
    if (/first|start|priority|begin|quick/.test(t)) {
        const x = sorted[0] as Gap;
        return `Start with "${x.title}". It is the best return for the time: about ${naira(x.impact)} a month in roughly ${x.days} days. First step: ${x.steps[0]}.`;
    }
    if (/direct|ota|commission|book/.test(t)) g = find(/direct/);
    else if (/lead|reply|response|slow|follow/.test(t)) g = find(/reply/);
    else if (/upsell|add-on|extra|breakfast/.test(t)) g = find(/upsell/);
    else if (/repeat|return|loyal|win/.test(t)) g = find(/win-back/);
    else if (/corporate|company|b2b/.test(t)) g = find(/corporate/);
    else if (/price|pricing|rate|occupancy/.test(t)) g = find(/dynamic/);
    if (!g) return `Across ${GAPS.length} gaps I found, the biggest upside is "${[...GAPS].sort((a, b) => b.impact - a.impact)[0]!.title}". Ask me about direct bookings, lead response, upsells, win-back, corporate accounts or pricing for a plan.`;
    return `${g.peers}\n\nWhat to do: ${g.steps.map((s, i) => `${i + 1}) ${s}`).join("  ")}\n\nEstimated upside: ${naira(g.impact)} a month, ${g.effort.toLowerCase()} effort, about ${g.days} days.`;
};

const SUGGESTED = ["What should I do first?", "Why am I losing direct bookings?", "How do I increase upsells?", "How do I bring back past customers?"];

/* ---------- small chart helpers ---------- */
const smooth = (pts: [number, number][]) => {
    if (pts.length === 0) return "";
    let d = `M${pts[0]![0]},${pts[0]![1]}`;
    for (let i = 1; i < pts.length; i++) {
        const [x0, y0] = pts[i - 1]!;
        const [x1, y1] = pts[i]!;
        const cx = (x0 + x1) / 2;
        d += ` C${cx},${y0} ${cx},${y1} ${x1},${y1}`;
    }
    return d;
};

const Ring: React.FC<{ pct: number }> = ({ pct }) => (
    <svg className={styles.axRing} viewBox="0 0 36 36" aria-label={`${pct}%`}>
        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="2.4" />
        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#5be0ec" strokeWidth="2.4" strokeLinecap="round" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="25" />
        <text x="18" y="21" textAnchor="middle" fontSize="8.5" fontWeight="600" fill="#fff">{pct}%</text>
    </svg>
);

interface Seg { label: string; value: number; color: string; sub?: string }
const Donut: React.FC<{ segs: Seg[]; center: string; caption: string; w?: number }> = ({ segs, center, caption, w = 5 }) => {
    const total = segs.reduce((s, x) => s + x.value, 0) || 1;
    let acc = 0;
    return (
        <svg className={styles.axDonut} viewBox="-3 -3 42 42" role="img" aria-label={caption}>
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#dfe7f3" strokeWidth={w} />
            {segs.map(s => {
                const pct = (s.value / total) * 100;
                const el = (
                    <circle key={s.label} cx="18" cy="18" r="15.9155" fill="none" stroke={s.color} strokeWidth={w}
                        strokeDasharray={`${Math.max(pct - 1.2, 0.1)} ${100 - Math.max(pct - 1.2, 0.1)}`} strokeDashoffset={25 - acc}>
                        <title>{`${s.label}: ${Math.round(pct)}%`}</title>
                    </circle>
                );
                acc += pct;
                return el;
            })}
            <text x="18" y="18.2" textAnchor="middle" fontSize="4.6" fontWeight="700" fill={NAVY}>{center}</text>
            <text x="18" y="22.4" textAnchor="middle" fontSize="2.6" fill="#6b7a99">{caption}</text>
        </svg>
    );
};

const Radar: React.FC<{ items: { label: string; v: number }[] }> = ({ items }) => {
    const cx = 110, cy = 100, R = 62;
    const pt = (i: number, r: number): [number, number] => {
        const a = ((-90 + (360 / items.length) * i) * Math.PI) / 180;
        return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
    };
    const poly = (f: (i: number) => number) => items.map((_, i) => pt(i, R * f(i)).join(",")).join(" ");
    return (
        <svg className={styles.axRadar} viewBox="-34 6 288 190" role="img" aria-label="You compared with the peer median on five measures">
            {[0.25, 0.5, 0.75, 1].map(k => <polygon key={k} points={poly(() => k)} fill="none" stroke="#d3dcec" strokeWidth="1" strokeDasharray={k === 1 ? "3 3" : undefined} />)}
            {items.map((_, i) => <line key={i} x1={cx} y1={cy} x2={pt(i, R)[0]} y2={pt(i, R)[1]} stroke="#d3dcec" />)}
            <polygon points={poly(i => clamp(items[i]!.v, 0, 100) / 100)} fill="rgba(20,184,198,.28)" stroke={TEAL} strokeWidth="2" />
            {items.map((it, i) => {
                const [x, y] = pt(i, R * clamp(it.v, 0, 100) / 100);
                const [lx, ly] = pt(i, R + 16);
                return (
                    <g key={it.label}>
                        <circle cx={x} cy={y} r="3.4" fill="#fff" stroke={TEAL} strokeWidth="2"><title>{`${it.label}: ${Math.round(it.v)}% of the peer median`}</title></circle>
                        <text x={lx} y={ly + 3} textAnchor={lx < cx - 4 ? "end" : lx > cx + 4 ? "start" : "middle"} fontSize="11.5" fill="#4b5877">{it.label}</text>
                    </g>
                );
            })}
        </svg>
    );
};

const AppAnalysis: React.FC<AppAnalysisProps> = ({ isOpen, onClose }) => {
    const [assistantOpen, setAssistantOpen] = useState(false);
    const view: View = assistantOpen ? "assistant" : "insights";

    const [tab, setTab] = useState<Tab>("all");
    const [basis, setBasis] = useState<Basis>("effort");
    const [plan, setPlan] = useState<string[]>(["g2", "g3"]);
    const [open, setOpen] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [showAll, setShowAll] = useState(true);
    const [showPlan, setShowPlan] = useState(true);
    const [month, setMonth] = useState(8);

    const [msgs, setMsgs] = useState<Msg[]>([
        { id: 0, from: "ai", text: `I compared your sales activity with ${PEER_SET_LOWER} and found ${GAPS.length} gaps. Ask me where to start.` },
    ]);
    const [draft, setDraft] = useState("");
    const [typing, setTyping] = useState(false);
    const idRef = useRef(1);
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [msgs, typing, view]);

    const total = useMemo(() => GAPS.reduce((s, g) => s + g.impact, 0), []);
    const inPlan = useMemo(() => GAPS.filter(g => plan.includes(g.id)), [plan]);
    const planTotal = inPlan.reduce((s, g) => s + g.impact, 0);
    const planPct = Math.round((planTotal / total) * 100);
    const quickTotal = GAPS.filter(g => g.effort === "Low").reduce((s, g) => s + g.impact, 0);

    const topGaps = useMemo(() => [...GAPS].sort((a, b) => b.impact - a.impact), []);
    const toggle = (id: string) => setPlan(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

    /* 12-month projection: each action reaches full impact about 4 months after it starts */
    const project = (items: Gap[]) => MONTHS.map((_, i) => items.reduce((s, g) => s + g.impact * clamp(((i + 1) * 30 - g.days) / 120, 0, 1), 0));
    const allSeries = useMemo(() => project(GAPS), []);
    const planSeries = useMemo(() => project(inPlan), [inPlan]);
    const quickSeries = useMemo(() => project(GAPS.filter(g => g.effort === "Low")), []);
    const barSeries = tab === "plan" ? planSeries : tab === "quick" ? quickSeries : allSeries;
    const barTotal = barSeries.reduce((s, v) => s + v, 0);
    const maxSeries = Math.max(...allSeries, 1);
    const hi = (i: number) => Math.abs(i - month) <= 1;

    const radarItems = BENCH.map(b => ({ label: b.label, v: (b.lowerBetter ? b.peers / b.you : b.you / b.peers) * 100 }));

    const effortSegs: Seg[] = (["Low", "Medium", "High"] as Effort[]).map((e, i) => ({
        label: `${e} effort`, value: GAPS.filter(g => g.effort === e).reduce((s, g) => s + g.impact, 0), color: [TEAL, BLUE, "#1e2a52"][i] as string,
    }));
    const moduleSegs: Seg[] = Array.from(new Set(GAPS.map(g => g.module))).map((m, i) => ({
        label: m, value: GAPS.filter(g => g.module === m).reduce((s, g) => s + g.impact, 0), color: [BLUE, TEAL, "#1e2a52"][i] as string,
    }));
    const budgetSegs = basis === "effort" ? effortSegs : moduleSegs;
    const worthSegs: Seg[] = topGaps.map((g, i) => ({ label: g.title, value: g.impact, color: DONUT[i] as string }));

    const speed = [
        { k: "7d", test: (g: Gap) => g.days <= 7 },
        { k: "14d", test: (g: Gap) => g.days > 7 && g.days <= 14 },
        { k: "30d", test: (g: Gap) => g.days > 14 },
    ].map(b => ({
        k: b.k,
        plan: GAPS.filter(g => b.test(g) && plan.includes(g.id)).reduce((s, g) => s + g.impact, 0),
        rest: GAPS.filter(g => b.test(g) && !plan.includes(g.id)).reduce((s, g) => s + g.impact, 0),
    }));
    const speedMax = Math.max(...speed.map(s => Math.max(s.plan, s.rest)), 1);
    const speedTop = speed.reduce((a, b) => (b.plan + b.rest > a.plan + a.rest ? b : a), speed[0]!).k;

    const copyPlan = async () => {
        const body = inPlan.map((g, i) => `${i + 1}. ${g.title} (${g.days} days, ~${naira(g.impact)}/month)\n   - ${g.steps.join("\n   - ")}`).join("\n");
        try { await navigator.clipboard.writeText(`30-day sales plan\n${body}\nEstimated upside: ${naira(planTotal)}/month`); setCopied(true); window.setTimeout(() => setCopied(false), 1600); } catch { /* clipboard blocked */ }
    };

    const send = (text: string) => {
        const q = text.trim();
        if (!q || typing) return;
        setMsgs(m => [...m, { id: idRef.current++, from: "me", text: q }]);
        setDraft("");
        setTyping(true);
        window.setTimeout(() => {
            setMsgs(m => [...m, { id: idRef.current++, from: "ai", text: askStrategyAssistant(q) }]);
            setTyping(false);
        }, 600);
    };

    /* inflow bar chart geometry */
    const IW = 560, IH = 190, ix0 = 50, ibase = 160, imaxH = 120;
    const slot = (IW - ix0 - 8) / 12;
    const ticks = [0, 0.25, 0.5, 0.75, 1];

    /* compare line chart geometry */
    const LW = 600, LH = 120, LP = 14;
    const lx = (i: number) => LP + (i * (LW - LP * 2)) / 11;
    const ly = (v: number) => LH - 10 - (v / maxSeries) * (LH - 30);
    const allPts = allSeries.map((v, i) => [lx(i), ly(v)] as [number, number]);
    const planPts = planSeries.map((v, i) => [lx(i), ly(v)] as [number, number]);

    /* hero sparkline */
    const spark = allSeries.map((v, i) => [8 + (i * 384) / 11, 64 - (v / maxSeries) * 52] as [number, number]);

    const renderMonths = () => (
        <div className={styles.axMonths} role="group" aria-label="Pick a month">
            {MONTHS.map((m, i) => (
                <button key={m} type="button" className={`${styles.axMonth} ${hi(i) ? styles.axMonthOn : ""}`} aria-pressed={month === i} onClick={() => setMonth(i)}>{m}</button>
            ))}
        </div>
    );

    return (
        <>
            <OverlayDrawer className={styles.Analysis}
                position={"start"}
                open={isOpen}
                onOpenChange={() => onClose()}
            >
                <DrawerBody>
                    <div className={styles.CFOEbt}>
                        <div className={styles.I34}>
                            <Button size="small" shape="circular" className={`${styles.axPill} ${view === "insights" ? styles.axPillOn : ""}`} appearance={view === "insights" ? "primary" : "secondary"} icon={<Home20Regular />} aria-label="Home" title="Home" onClick={() => setAssistantOpen(false)} />
                            <Button size="small" shape="circular" className={`${styles.axPill} ${view === "assistant" ? styles.axPillOn : ""}`} appearance={view === "assistant" ? "primary" : "secondary"} icon={<Sparkle20Regular />} aria-label="Assistant" title="Assistant" onClick={() => setAssistantOpen(!assistantOpen)} />
                        </div>

                        <div className={`${styles.Qwren} ${styles.axMain}`}>
                            {view === "assistant" ? (
                                <div className={styles.HMe36}>
                                    <div className={styles.axChat}>
                                        <p className={styles.axPeerSet}>Compared with: {PEER_SET}</p>
                                        <div className={styles.axChatLog} aria-live="polite">
                                            {msgs.map(m => (
                                                <div key={m.id} className={`${styles.axBubble} ${m.from === "me" ? styles.axMe : styles.axAi}`}>{m.text}</div>
                                            ))}
                                            {typing && <div className={`${styles.axBubble} ${styles.axAi} ${styles.axDots}`} aria-label="Assistant is typing"><i /><i /><i /></div>}
                                            <div ref={endRef} />
                                        </div>
                                        <div className={styles.axChips}>
                                            {SUGGESTED.map(s => (
                                                <button key={s} type="button" className={styles.axChip} onClick={() => send(s)}>{s}</button>
                                            ))}
                                        </div>
                                        <form className={styles.axComposer} onSubmit={e => { e.preventDefault(); send(draft); }}>
                                            <input className={styles.axInput} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Ask how to sell more…" aria-label="Ask the assistant" />
                                            <Button size="small" appearance="primary" type="submit" icon={<Send20Regular />} disabled={!draft.trim() || typing}>Send</Button>
                                        </form>
                                    </div>
                                </div>
                            ) : (
                                <div className={`${styles.Wrtej} ${styles.HMe36} ${styles.axWrap}`}>
                                    <div className={`${styles.I35} ${styles.axFill}`}>
                                        <p className={styles.axPeerSet}>Compared with: {PEER_SET}</p>
                                        <div className={styles.axBoard}>

                                            {/* ===== 1. balance card ===== */}
                                            <section className={`${styles.axCard} ${styles.axHero}`}>
                                                <div className={styles.axHeroTop}>
                                                    <div className={styles.axHeroLeft}>
                                                        <div className={styles.axHeroRow}><span>Plan</span><b>{short(planTotal)}</b></div>
                                                        <div className={styles.axHeroChip}><Payment20Regular /><span>{GAPS.length} gaps vs peers</span></div>
                                                    </div>
                                                    <div className={styles.axHeroLoad}><span>Plan<br />coverage</span><Ring pct={planPct} /></div>
                                                </div>
                                                <div className={styles.axHeroBig}>{short(total)}<small>Total upside</small></div>
                                                <svg className={styles.axSpark} viewBox="0 0 400 76" aria-hidden="true">
                                                    <defs>
                                                        <linearGradient id="axSparkFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#5be0ec" stopOpacity=".35" /><stop offset="1" stopColor="#5be0ec" stopOpacity="0" /></linearGradient>
                                                    </defs>
                                                    <path d={`${smooth(spark)} L392,76 L8,76 Z`} fill="url(#axSparkFill)" />
                                                    <path d={smooth(spark)} fill="none" stroke="#5be0ec" strokeWidth="2" />
                                                    {[3, 6, 9].map(i => <g key={i}><line x1={spark[i]![0]} x2={spark[i]![0]} y1={spark[i]![1]} y2="76" stroke="#5be0ec" strokeOpacity=".5" strokeDasharray="2 3" /><circle cx={spark[i]![0]} cy={spark[i]![1]} r="4" fill="#0f2557" stroke="#5be0ec" strokeWidth="2" /></g>)}
                                                </svg>
                                                <div className={styles.axHeroFoot}><span>Quick wins</span><b>+{short(quickTotal)}</b></div>
                                            </section>

                                            {/* ===== 2. inflow bars ===== */}
                                            <section className={`${styles.axCard} ${styles.axInflow}`}>
                                                <header className={styles.axCardHead}>
                                                    <h3>Upside</h3>
                                                    <div className={styles.axSeg} role="group" aria-label="Which gaps to chart">
                                                        {([["all", "All gaps"], ["plan", "Your plan"], ["quick", "Quick wins"]] as [Tab, string][]).map(([k, l]) => (
                                                            <button key={k} type="button" aria-pressed={tab === k} className={`${styles.axSegBtn} ${tab === k ? styles.axSegOn : ""}`} onClick={() => setTab(k)}>{l}</button>
                                                        ))}
                                                    </div>
                                                    <b className={styles.axHeadTotal}>{short(barTotal)}</b>
                                                </header>
                                                <svg className={styles.axBars} viewBox={`0 0 ${IW} ${IH}`} role="img" aria-label="Projected monthly upside">
                                                    <defs>
                                                        <linearGradient id="axTeal" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#35d3df" /><stop offset="1" stopColor="#0e93b5" /></linearGradient>
                                                    </defs>
                                                    {ticks.map(k => (
                                                        <g key={k}>
                                                            <line x1={ix0} x2={IW} y1={ibase - imaxH * k} y2={ibase - imaxH * k} stroke="#dfe7f3" strokeWidth="1" />
                                                            <text x={ix0 - 6} y={ibase - imaxH * k + 3} textAnchor="end" fontSize="11" fill="#6b7a99">{k === 0 ? "0" : short(maxSeries * k)}</text>
                                                        </g>
                                                    ))}
                                                    {barSeries.map((v, i) => {
                                                        const h = Math.max((v / maxSeries) * imaxH, 2);
                                                        const x = ix0 + i * slot + slot * 0.2;
                                                        const w = slot * 0.6;
                                                        return (
                                                            <g key={MONTHS[i]} onClick={() => setMonth(i)} style={{ cursor: "pointer" }}>
                                                                <rect x={x} y={ibase - h} width={w} height={h} rx={Math.min(6, w / 2)} fill={hi(i) ? "url(#axTeal)" : NAVY}><title>{`${MONTHS[i]}: ${naira(Math.round(v))}`}</title></rect>
                                                                {hi(i) && <text x={x + w / 2} y={ibase - h - 5} textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#0e7f99">{short(v)}</text>}
                                                            </g>
                                                        );
                                                    })}
                                                </svg>
                                                {renderMonths()}
                                            </section>

                                            {/* ===== 3. structure radar ===== */}
                                            <section className={`${styles.axCard} ${styles.axStruct}`}>
                                                <header className={styles.axCardHead}>
                                                    <h3>Structure</h3>
                                                </header>
                                                <p className={styles.axSub}>You vs peers</p>
                                                <Radar items={radarItems} />
                                            </section>

                                            {/* ===== 4. goals ===== */}
                                            <section className={`${styles.axCard} ${styles.axGoals}`}>
                                                <header className={styles.axCardHead}>
                                                    <h3>Gaps</h3>
                                                    <b className={styles.axGoalsSum}>{short(planTotal)} / {short(total)}</b>
                                                    <b className={styles.axGoalsPct}>{planPct}%</b>
                                                </header>
                                                <div className={styles.axTrack}><span style={{ width: `${planPct}%` }} /></div>
                                                <ul className={styles.axRows}>
                                                    {topGaps.map((g, gi) => {
                                                        const added = plan.includes(g.id);
                                                        const isOpen = open === g.id;
                                                        return (
                                                            <li key={g.id} className={styles.axItem}>
                                                                <div className={styles.axRow}>
                                                                    <button type="button" className={`${styles.axCheck} ${added ? styles.axCheckOn : ""}`} aria-pressed={added} aria-label={`${added ? "Remove" : "Add"} ${g.title} ${added ? "from" : "to"} plan`} onClick={() => toggle(g.id)}>{added ? <Checkmark16Regular /> : <Add16Regular />}</button>
                                                                    <button type="button" className={styles.axRowName} aria-expanded={isOpen} title={g.title} onClick={() => setOpen(isOpen ? null : g.id)}>{g.title}</button>
                                                                    <span className={styles.axRowNum}><b>{short(g.impact)}</b><em>{Math.round((g.impact / total) * 100)}%</em></span>
                                                                </div>
                                                                {isOpen && (
                                                                    <div className={`${styles.axDetail} ${gi >= 3 ? styles.axDetailUp : ""}`} role="dialog" aria-label={g.title}>
                                                                        <div className={styles.axMeta}>
                                                                            <span className={`${styles.axTag} ${g.you === "Not doing" ? styles.axTagRed : styles.axTagAmber}`}>{g.you}</span>
                                                                            <span className={styles.axTag}>{g.adoption}% of peers</span>
                                                                            <span className={styles.axTag}>{g.effort} effort · {g.days} days</span>
                                                                        </div>
                                                                        <p>{g.peers}</p>
                                                                        <ol>{g.steps.map(s => <li key={s}>{s}</li>)}</ol>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </section>

                                            {/* ===== 5. budget ===== */}
                                            <section className={`${styles.axCard} ${styles.axBudget}`}>
                                                <header className={styles.axCardHead}>
                                                    <h3>Mix</h3>
                                                    <div className={styles.axRound}>
                                                        <button type="button" aria-pressed={basis === "effort"} aria-label="Split by effort" title="By effort" className={`${styles.axRoundBtn} ${basis === "effort" ? styles.axRoundOn : ""}`} onClick={() => setBasis("effort")}><Flash20Regular /></button>
                                                        <button type="button" aria-pressed={basis === "module"} aria-label="Split by module" title="By module" className={`${styles.axRoundBtn} ${basis === "module" ? styles.axRoundOn : ""}`} onClick={() => setBasis("module")}><Apps20Regular /></button>
                                                    </div>
                                                </header>
                                                <div className={styles.axBudgetBody}>
                                                    <div className={styles.axDonutWrap}><Donut segs={budgetSegs} center={short(total)} caption="total upside" /></div>
                                                    <div className={styles.axBudgetSide}>
                                                        <div className={styles.axMeter}><span>In plan</span><div className={styles.axTrackSm}><i style={{ width: `${planPct}%`, background: TEAL }} /></div><b>{planPct}%</b></div>
                                                        <div className={styles.axMeter}><span>Not yet</span><div className={styles.axTrackSm}><i style={{ width: `${100 - planPct}%`, background: NAVY }} /></div><b>{100 - planPct}%</b></div>
                                                        <ul className={styles.axLegendList}>
                                                            {budgetSegs.map(s => (
                                                                <li key={s.label}><i style={{ background: s.color }} /><span>{s.label}</span><b>{Math.round((s.value / total) * 100)}%</b><em>{short(s.value)}</em></li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div className={styles.axBalance}>
                                                        <b>{short(planTotal)}</b><span>Plan upside</span>
                                                        <Button size="small" appearance="primary" icon={copied ? <Checkmark16Regular /> : <Copy20Regular />} disabled={inPlan.length === 0} onClick={copyPlan}>{copied ? "Copied" : "Copy plan"}</Button>
                                                    </div>
                                                </div>
                                            </section>

                                            {/* ===== 6. costs donut ===== */}
                                            <section className={`${styles.axCard} ${styles.axCosts}`}>
                                                <header className={styles.axCardHead}><h3>Where the money is</h3></header>
                                                <div className={styles.axDonutBig}><Donut segs={worthSegs} center={short(total)} caption="Total" w={7} /></div>
                                                <ul className={styles.axLegendList}>
                                                    {worthSegs.map(s => (
                                                        <li key={s.label}><i style={{ background: s.color }} /><span title={s.label}>{s.label}</span><b>{Math.round((s.value / total) * 100)}%</b><em>{short(s.value)}</em></li>
                                                    ))}
                                                </ul>
                                            </section>

                                            {/* ===== 7. compare ===== */}
                                            <section className={`${styles.axCard} ${styles.axCompare}`}>
                                                <header className={styles.axCardHead}>
                                                    <h3>Compare upside <em className={styles.axHeadVal}>{short(allSeries[month]!)}</em></h3>
                                                    <div className={styles.axToggles}>
                                                        <button type="button" className={styles.axToggle} aria-pressed={showAll} onClick={() => setShowAll(v => !v)}>All gaps<i className={`${styles.axSw} ${showAll ? styles.axSwOn : ""}`} /></button>
                                                        <em className={styles.axHeadVal}>{short(planSeries[month]!)}</em>
                                                        <button type="button" className={styles.axToggle} aria-pressed={showPlan} onClick={() => setShowPlan(v => !v)}>Your plan<i className={`${styles.axSw} ${showPlan ? styles.axSwOn : ""}`} /></button>
                                                    </div>
                                                </header>
                                                <svg className={styles.axLine} viewBox={`0 0 ${LW} ${LH}`} role="img" aria-label="Projected monthly upside over twelve months">
                                                    <defs>
                                                        <linearGradient id="axLineFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={BLUE} stopOpacity=".3" /><stop offset="1" stopColor={BLUE} stopOpacity="0" /></linearGradient>
                                                    </defs>
                                                    {showPlan && <path d={`${smooth(planPts)} L${lx(11)},${LH - 10} L${lx(0)},${LH - 10} Z`} fill="url(#axLineFill)" />}
                                                    {showAll && <path d={smooth(allPts)} fill="none" stroke={BLUE} strokeWidth="2.4" />}
                                                    {showPlan && <path d={smooth(planPts)} fill="none" stroke={TEAL} strokeWidth="2.4" />}
                                                    {MONTHS.map((_, i) => hi(i) && (
                                                        <g key={i}>
                                                            <line x1={lx(i)} x2={lx(i)} y1="6" y2={LH - 10} stroke="#9bb0d3" strokeDasharray="3 3" />
                                                            {showAll && <circle cx={allPts[i]![0]} cy={allPts[i]![1]} r="4" fill="#fff" stroke={BLUE} strokeWidth="2" />}
                                                            {showPlan && <circle cx={planPts[i]![0]} cy={planPts[i]![1]} r="4" fill="#fff" stroke={TEAL} strokeWidth="2" />}
                                                        </g>
                                                    ))}
                                                </svg>
                                                {renderMonths()}
                                            </section>

                                            {/* ===== 8. annual plans ===== */}
                                            <section className={`${styles.axCard} ${styles.axAnnual}`}>
                                                <header className={styles.axCardHead}><h3>How fast it pays</h3></header>
                                                <svg className={styles.axSpeedSvg} viewBox="0 0 220 140" role="img" aria-label="Upside by how many days each action takes to start">
                                                    <defs>
                                                        <linearGradient id="axBlue" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4d8cf0" /><stop offset="1" stopColor="#2358b8" /></linearGradient>
                                                    </defs>
                                                    {speed.map((s, i) => {
                                                        const x0 = 22 + i * 64;
                                                        const hp = Math.max((s.plan / speedMax) * 80, 2);
                                                        const hr = Math.max((s.rest / speedMax) * 80, 2);
                                                        const top = Math.max(s.plan, s.rest);
                                                        return (
                                                            <g key={s.k}>
                                                                <rect x={x0} y={108 - hr} width="16" height={hr} rx="4" fill="#dce8fb" stroke="#9ec1ee"><title>{`Not in plan, ${s.k}: ${short(s.rest)}`}</title></rect>
                                                                <rect x={x0 + 20} y={108 - hp} width="16" height={hp} rx="4" fill="url(#axBlue)"><title>{`In plan, ${s.k}: ${short(s.plan)}`}</title></rect>
                                                                <text x={x0 + 18} y={108 - Math.max(hp, hr) - 5} textAnchor="middle" fontSize="10" fill="#4b5877">{short(top)}</text>
                                                                <text x={x0 + 18} y="126" textAnchor="middle" fontSize="11" fontWeight={s.k === speedTop ? 700 : 500} fill={s.k === speedTop ? NAVY : "#6b7a99"}>{s.k}</text>
                                                            </g>
                                                        );
                                                    })}
                                                </svg>
                                            </section>
                                        </div>
                                    </div>

                                    {/* kept so your layout still has its side column – hidden because every card is now in the board */}
                                    <aside className={`${styles.I36} ${styles.axGone}`} aria-hidden="true" />
                                </div>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </>
    );
};

export default AppAnalysis;